import { create } from 'zustand';
import { fetchUserPreferences, saveUserPreference } from '../lib/api';

/**
 * Interface preferences of the signed-in user (Pascal, 28/09/2026: column
 * widths « mémorisées pour l'utilisateur »). The server keeps them per
 * account; a per-account copy in the browser shows them at once while the
 * server answers. Writes are grouped (one save per key after the drag).
 */
interface PreferencesState {
  owner: string;
  loaded: boolean;
  prefs: Record<string, unknown>;
  hydrate: (owner: string) => Promise<void>;
  setPref: (key: string, value: unknown) => void;
}

const SAVE_DELAY_MS = 600;
const timers = new Map<string, ReturnType<typeof setTimeout>>();

function mirrorKey(owner: string): string {
  return `aircs-prefs:${owner.toLowerCase()}`;
}

function readMirror(owner: string): Record<string, unknown> {
  try {
    const raw = window.localStorage.getItem(mirrorKey(owner));
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === 'object' ? parsed as Record<string, unknown> : {};
  } catch {
    return {};
  }
}

function writeMirror(owner: string, prefs: Record<string, unknown>): void {
  try {
    window.localStorage.setItem(mirrorKey(owner), JSON.stringify(prefs));
  } catch {
    // Private browsing / quota: the server copy still holds the preference.
  }
}

export const usePreferencesStore = create<PreferencesState>((set, get) => ({
  owner: '',
  loaded: false,
  prefs: {},
  hydrate: async (owner) => {
    if (!owner || get().owner === owner) return;
    set({ owner, loaded: false, prefs: readMirror(owner) });
    try {
      const remote = await fetchUserPreferences();
      if (get().owner !== owner) return;
      const prefs = { ...get().prefs, ...remote };
      set({ prefs, loaded: true });
      writeMirror(owner, prefs);
    } catch {
      set({ loaded: true });
    }
  },
  setPref: (key, value) => {
    const { owner } = get();
    const prefs = { ...get().prefs, [key]: value };
    set({ prefs });
    if (!owner) return;
    writeMirror(owner, prefs);
    const pending = timers.get(key);
    if (pending) clearTimeout(pending);
    timers.set(key, setTimeout(() => {
      timers.delete(key);
      void saveUserPreference(key, value).catch(() => undefined);
    }, SAVE_DELAY_MS));
  },
}));
